/**
 * PATH: packages/email-hub/src/feature/personal/PersonalInboxPage.tsx
 * FILE_ID: EMAILHUB-PERSONAL-INBOXPAGE-001
 * PURPOSE: The cross-org "Personal" aggregate inbox — every email the operator
 *          can see across EVERY org they belong to, in one list, each row
 *          badged with its source org and offering a cross-tag "Add to board".
 * WHY:     FEAT-2026-06-22-CROSS-ORG-PERSONAL — operator runs 7 mailboxes
 *          across 3 orgs: "I want to just use emails structured" without
 *          org-switching, with no cross-org bleed.
 * FLOW:
 *   usePersonalEmails (no tenant → all member orgs) +
 *   usePersonalPins (pinned-count per row) → list of <PersonalEmailRow> →
 *   "Add to board" → <AddToBoardDialog> (cross-org picker → private pin)
 * DEPENDENCIES:
 *   - usePersonalData: the cross-org inbox + pins queries.
 *   - PersonalEmailRow / AddToBoardDialog: row + cross-tag picker.
 * RELATED:
 *   - PersonalBoardsPage.tsx: the per-org board mirror these pins land on.
 */
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertCircle, Inbox, LayoutGrid, Loader2 } from 'lucide-react'

import { useApiAuth } from '../../_internal/components/ApiAuthGate'
import { useToast } from '../../_internal/components/Toast'
import { openEmailInOrg } from '../cross-org-actions/openEmailInOrg'
import { AddToBoardDialog } from './AddToBoardDialog'
import { emailPath } from '../lib/basePath'
import { PersonalEmailRow } from './PersonalEmailRow'
import { PersonalSetupCard } from './PersonalSetupCard'
import { countPinsForEmailCopies } from './personalInboxSelectors'
import {
  usePersonalEmails,
  usePersonalPins,
  type PersonalEmail,
} from './usePersonalData'

export function PersonalInboxPage() {
  const { data, isLoading, isError, refetch } = usePersonalEmails()
  const { data: pins } = usePersonalPins()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [dialogEmail, setDialogEmail] = useState<PersonalEmail | null>(null)
  const navigate = useNavigate()
  const { session, switchOrganisation } = useApiAuth()
  const { toast } = useToast()

  // Memoised so the derived list is referentially stable across renders
  // (it feeds the orgCounts useMemo dependency array).
  const emails = useMemo(() => data?.results ?? [], [data])

  // How many boards each email is already pinned onto (drives the row hint).
  const pinCountByEmail = useMemo(() => {
    const map = new Map<string, number>()
    for (const pin of pins ?? []) {
      map.set(pin.email, (map.get(pin.email) ?? 0) + 1)
    }
    return map
  }, [pins])

  // Per-org row counts for the summary strip ("structured" overview).
  const orgCounts = useMemo(() => {
    const map = new Map<number, { name: string; count: number }>()
    for (const e of emails) {
      const copies = e.present_in_orgs.length > 0
        ? e.present_in_orgs
        : [{ org_id: e.source_org_id, org_name: e.source_org_name }]
      for (const copy of copies) {
        const entry = map.get(copy.org_id)
        if (entry) entry.count += 1
        else map.set(copy.org_id, { name: copy.org_name, count: 1 })
      }
    }
    return [...map.entries()].sort((a, b) => b[1].count - a[1].count)
  }, [emails])

  const openOrgCopy = async (
    copy: PersonalEmail['present_in_orgs'][number],
  ) => {
    try {
      const outcome = await openEmailInOrg({
        emailId: copy.email_id,
        targetOrgId: copy.org_id,
        targetOrgName: copy.org_name,
        currentOrgId: session?.organisation?.id ?? null,
        switchOrganisation,
        navigate,
      })
      if (outcome === 'switch-unavailable') {
        toast('Open this email from the organisation switcher first.', 'info')
      }
    } catch {
      toast(`Could not switch to ${copy.org_name}.`, 'error')
    }
  }

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-3">
        <div className="flex items-center gap-2">
          <Inbox className="h-5 w-5 text-slate-500" />
          <div>
            <h1 className="text-base font-semibold text-slate-900">
              All inboxes
            </h1>
            <p className="text-xs text-slate-500">
              One email list across your organisations, without repeated copies.
            </p>
          </div>
        </div>
        <Link
          to={emailPath('/personal/boards')}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <LayoutGrid className="h-4 w-4" />
          Boards
        </Link>
      </header>

      <PersonalSetupCard />

      {orgCounts.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-white/60 px-5 py-2">
          {orgCounts.map(([orgId, { name, count }]) => (
            <span
              key={orgId}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600"
            >
              {name}
              <span className="text-slate-400">{count}</span>
            </span>
          ))}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your mail…
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center gap-3 py-16 text-sm text-slate-500">
            <AlertCircle className="h-6 w-6 text-rose-400" />
            <p>Could not load your Personal inbox.</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="rounded-lg border border-slate-200 px-3 py-1.5 font-medium hover:bg-white"
            >
              Try again
            </button>
          </div>
        ) : emails.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-sm text-slate-400">
            <Inbox className="h-6 w-6" />
            <p>No mail across your organisations yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {emails.map((email) => (
              <PersonalEmailRow
                key={`${email.source_org_id}:${email.id}`}
                email={email}
                isSelected={selectedId === email.id}
                onSelect={() => setSelectedId(email.id)}
                onAddToBoard={setDialogEmail}
                onOpenOrgCopy={(copy) => void openOrgCopy(copy)}
                pinnedCount={countPinsForEmailCopies(email, pinCountByEmail)}
              />
            ))}
            {data?.next && (
              <div className="px-5 py-3 text-center text-xs text-slate-400">
                Showing your most recent mail. Refine in a single organisation
                for the full archive.
              </div>
            )}
          </div>
        )}
      </div>

      {dialogEmail && (
        <AddToBoardDialog
          email={dialogEmail}
          onClose={() => setDialogEmail(null)}
        />
      )}
    </div>
  )
}
